import React, { useEffect, useRef, useState } from "react";
import Card from "../../../components/Card/Card";
import List from "../../../components/List/List";
import Pagination from "../../../components/Pagination/Pagination";
import SyncIcon from "../../../components/SyncIcon/SyncIcon";
import { useSSEContext } from "../../../contexts/sse";
import { useUserStore } from "../../../contexts/userStore";
import { ModuleType, ModuleTypeText } from "../../../enums/moduleType";
import AccessModuleService from "../../../services/AccessModuleService";
import "./ListAccessPoints.css";
import { AccessIcons } from "../../../utils/accessIcons";
import { listRfSlots } from "../../../helpers/AccessPointParser";
import { TiArrowLeftThick, TiArrowRightThick } from "react-icons/ti";
import { AccessUnit, AccessUnitOptions } from "../../../components/AccessUnitSelector/AccessUnitSelector";
import { useTranslation } from "react-i18next";

function parseLicenseStatus(data, old) {
	if (!data?.content?.result?.status) {
		return old;
	}
	if (data.content.result.status === 'success') {
		return 'synced';
	}
	return data.content.result.status;
}

const ListAccessPoints = (props) => {
	const [accessPoint, setAccessPoint] = useState([]);
	const [maxPage, setMaxPage] = useState(1);
	const { selectedCondominio, selectedLicenceId, condominiums } = useUserStore();
	const [currentPage, setCurrentPage] = useState(1);
	const { sseReceived } = useSSEContext();
	const [collumnClicked, setCollumnClicked] = useState({
		collumnTitle: "createdAt",
		orderDirection: "DESC",
	});
	const [isLoading, setIsLoading] = useState(true);
	const cancelTokenRef = useRef(null);
	const { t } = useTranslation();

	const getAccessPoints = async (
		page,
		searchText,
		selectedCondominio,
		selectedLicenceId,
		orderBy,
		orderDirection,
	) => {
		if (selectedCondominio === 0 || !selectedLicenceId) return;
		if (cancelTokenRef.current) {
			cancelTokenRef.current.abort();
		}
		cancelTokenRef.current = new AbortController();
		setIsLoading(true);
		const response = await AccessModuleService.listPaginated(
			page,
			searchText,
			selectedCondominio,
			selectedLicenceId,
			orderBy,
			orderDirection,
			cancelTokenRef.current.signal
		);
		setIsLoading(false);
		if (response?.data?.items) {
			const items = listRfSlots(response.data?.items);
			setAccessPoint(items);
			setCurrentPage(page);
			setMaxPage(response.data?.meta?.totalPages);
		} else {
			setAccessPoint([]);
			setCurrentPage(1);
			setMaxPage(1);
		}
	};

	useEffect(() => {
		getAccessPoints(
			currentPage,
			props.filters,
			selectedCondominio,
			selectedLicenceId,
			collumnClicked.collumnTitle,
			collumnClicked.orderDirection,
		);
	}, [
		props.filters,
		selectedCondominio,
		selectedLicenceId,
		collumnClicked,
		props.refreshKey,
		currentPage,
	]);

	useEffect(() => {
		if (sseReceived?.action === "replyAccessPointReceived") {
			if (accessPoint.find(ap => ap.mqtt_id === sseReceived.content?.mqtt_id)) {
				setAccessPoint(prev => prev.map(item => {
					return {
						...item,
						status: item.mqtt_id === sseReceived.content?.mqtt_id ? parseLicenseStatus(sseReceived, item.status) : item.status
					}
				}));
			} else {
				getAccessPoints(
					currentPage,
					props.filters,
					selectedCondominio,
					selectedLicenceId,
					collumnClicked.collumnTitle,
					collumnClicked.orderDirection,
				);
			}
		}
	}, [
		sseReceived,
	]);

	const safeData = accessPoint.map((item) => ({
		...item,
		address: item.address === '' || !item.address ? null : item.address,
		serial: item.serial === '' || !item.serial ? null : item.serial,
		status: item.status ?? t("common.doesNotContain"),
	}));

	return (
		<React.Fragment>
			<div className="list-container list-visibility">
				<List
					isLoading={isLoading}
					// className="table-auto!"
					handleCollumnTitleClick={(collumnIndex) =>
						setCollumnClicked(collumnIndex)
					}
					listEntityName="accessPoints"
					collumsObjIndex={[
						{ collumnLabel: t("common.name"), index: "name", type: "text" },
						{
							collumnLabel: t("common.enabled"),
							index: "enabled",
							type: "enabled",
						},
						{
							collumnLabel: t("common.destinations"), type: "custom", custom: ({ row }) => {
								return (
									<div className="wrapper-access-destiny">
										<span className="entry">
											<TiArrowRightThick />
											{row.entry_ref ? (Object.values(AccessUnit).includes(row.entry_ref) ? t(AccessUnitOptions[row.entry_ref]) : condominiums.find(unit => unit.id === `${row.entry_ref}`)?.name) : <span>{t("common.doesNotContain")}</span>}
										</span>
										<span className="exit">
											<TiArrowLeftThick />
											{row.exit_ref ? (Object.values(AccessUnit).includes(row.exit_ref) ? t(AccessUnitOptions[row.exit_ref]) : condominiums.find(unit => unit.id === `${row.exit_ref}`)?.name) : <span>{t("common.doesNotContain")}</span>}
										</span>
									</div>
								);
							}
						},
						{
							collumnLabel: t("common.addressAndSerial"), type: "custom", custom: ({ row }) => {
								return (row.address ?? row.serial) ?? <span style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</span>;
							}
						},
						{ collumnLabel: t("common.type"), index: "type", type: "changeValue" },
						{ collumnLabel: t("common.status"), index: "status", type: "sync-status" },
					]}
					changeColumnValue={Object.entries(ModuleTypeText).map(
						([key, text]) => ({ old: Number(key), new: t(text), chip: true, icon: AccessIcons[key] }),
					)}
					data={safeData.map(item => ({
						...item,
						enabled: (Object.values(item).some(field => typeof field === 'object' && field?.enabled)),
					}))}
				/>
			</div>
			<div className="card-visibility">
				{accessPoint.map((ap, index) => (
					<Card
						key={index}
						delete={() => AccessModuleService.deleteAccessPoint(ap.id)}
						navigateOnDelete={"../consulta-access-points"}
						navigateOnEdit={"../cadastro-access-points"}
						data={ap}
						aditionalButton1={
							<SyncIcon
								isCard={true}
								syncStatus={ap.status}
								data={ap}
								listEntityName={"accessPoints"}
							/>
						}
					>
						<p className="card-content-title">{ap.name ?? t("common.doesNotContain")}</p>
						<p className="card-content">
							{t("common.address")}: {ap.address_rs485 ?? t("common.doesNotContain")}
						</p>
						<p className="card-content">{t("common.type")}: {ap.type ?? t("common.doesNotContain")}</p>
						<p className="card-content">
							{t("common.enabled")}: {ap.enabled ? t("common.yes") : t("common.no")}
						</p>
					</Card>
				))}
			</div>
			<Pagination
				rowsPerPage={8}
				maxPage={maxPage}
				changePage={(page) =>
					getAccessPoints(
						page,
						props.filters,
						selectedCondominio,
						selectedLicenceId,
						collumnClicked.collumnTitle,
						collumnClicked.orderDirection,
					)
				}
			/>
		</React.Fragment>
	);
};

export default ListAccessPoints;
